import React, { useContext, useState, useEffect } from 'react';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import {
	IEditCustomerContext,
	EditCustomerContext,
} from 'modules/CustomersAndRestaurants/Customers/EditCustomer/editCustomerState/editCustomerContext';
import { BpaContext, IBpaContext } from '../bpaState/bpaContext';
import { NEW_BPA_BLOCK_PREFIX, NEW_BPA_TARIFF_PREFIX } from 'appConstants';
import { ICustomerBPAForUpdate, CardBrands, BpaPaymentSystem } from 'types';
import { Form } from 'antd';
import type { FormProps } from 'antd';
import { BpaBlock } from './components/BpaBlock/BpaBlock';
import { BtnBlock } from './components/BtnBlock';
import Text from 'components/Text/Text';
import style from './InputFields.module.scss';
import { preventDefaultForEnterKey } from 'utils/helpers';

type FieldData = Parameters<NonNullable<FormProps['onFieldsChange']>>[0];

export const InputFields = () => {
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);
	const { customerId } = useContext<IEditCustomerContext>(EditCustomerContext);
	const { changeTypeOfModalWindow, setDataForSubmit, bpa } = useContext<IBpaContext>(BpaContext);
	const [allFields, setAllFields] = useState<FieldData>([]);
	const [form] = Form.useForm();

	useEffect(() => {
		bpa.forEach((bpaSet) => {
			form.setFieldsValue({
				[`name-${bpaSet.id}`]: bpaSet.name,
				[`recipientAccountNumber-${bpaSet.id}`]: bpaSet.recipientAccountNumber,
				[`recipientName-${bpaSet.id}`]: bpaSet.recipientName,
				[`recipientBank-${bpaSet.id}`]: bpaSet.recipientBank,
				[`recipientBankCity-${bpaSet.id}`]: bpaSet.recipientBankCity,
				[`recipientInn-${bpaSet.id}`]: bpaSet.recipientInn,
				[`recipientBic-${bpaSet.id}`]: bpaSet.recipientBic,
				[`recipientCorrespondentAccount-${bpaSet.id}`]: bpaSet.recipientCorrespondentAccount,
				[`paymentDetails-${bpaSet.id}`]: bpaSet.paymentDetails,
				[`cardContractNumber-${bpaSet.id}`]: bpaSet.cardContractNumber,
				[`sbpContractNumber-${bpaSet.id}`]: bpaSet.sbpContractNumber,
			});

			bpaSet.tariffs.forEach((extendedTariff) => {
				form.setFieldsValue({
					[`typeOfBpaPaymentSystem-${extendedTariff.id}`]: extendedTariff.typeOfBpaPaymentSystem,
				});

				extendedTariff.data.forEach((brands) => {
					form.setFieldsValue({
						[`cardBrand-${brands.id}`]: brands.cardBrand,
						[`bankFee-${brands.id}`]: brands.bankFee,
						[`kmFee-${brands.id}`]: brands.kmFee,
					});
				});
			});
		});
	}, [bpa]);

	const submit = async () => {
		setMainFormValidationFail(false);

		const finalBpa: ICustomerBPAForUpdate[] = bpa.map((el) => {
			const isNewBpaSet = el.id.slice(0, NEW_BPA_BLOCK_PREFIX.length) === NEW_BPA_BLOCK_PREFIX;

			const tariffs = el.tariffs.reduce((obj: ICustomerBPAForUpdate['tariffs'], item) => {
				item.data.forEach((tariff) => {
					const isNewTariff = tariff.id.slice(0, NEW_BPA_TARIFF_PREFIX.length) === NEW_BPA_TARIFF_PREFIX;

					obj.push({
						id: isNewTariff ? null : tariff.id,
						bpaPaymentSystem: item.typeOfBpaPaymentSystem,
						cardBrand: tariff.cardBrand,
						bankFee: tariff.bankFee || 0,
						kmFee: tariff.kmFee || 0,
					});
				});
				return obj;
			}, []);

			return {
				id: isNewBpaSet ? null : el.id,
				name: el.name || '',
				recipientAccountNumber: el.recipientAccountNumber || '',
				recipientName: el.recipientName || '',
				recipientBank: el.recipientBank || '',
				recipientBankCity: el.recipientBankCity || '',
				recipientInn: el.recipientInn || '',
				recipientBic: el.recipientBic || '',
				recipientCorrespondentAccount: el.recipientCorrespondentAccount || '',
				paymentDetails: el.paymentDetails || '',
				cardContractNumber: el.cardContractNumber || '',
				sbpContractNumber: el.sbpContractNumber || '',
				tariffs,
			};
		});

		setDataForSubmit({
			customerId,
			bpaSets: finalBpa,
		});
		additionalValidationErrors.length
			? setMainFormValidationFail(true)
			: changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFormValidationFail = () => {
		setMainFormValidationFail(true);
	};

	const handleFormFieldsChange = (_: FieldData, allFields: FieldData) => {
		setAllFields(allFields);
	};

	return (
		<div data-test="InputFields">
			<Form
				form={form}
				name="basic"
				onFinish={submit}
				onFinishFailed={handleFormValidationFail}
				onKeyPress={preventDefaultForEnterKey}
				onFieldsChange={handleFormFieldsChange}
				layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="bpaSetHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<BpaBlock allFields={allFields} />
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export type { FieldData };
